import React, { useContext, useState } from 'react';
import './App.css';
import {
  BrowserRouter as Router, Switch, useHistory
} from 'react-router-dom';

import Loader from "react-loader-advanced";
import { ToastContainer } from 'react-toastify';

import ScrollToTop from "react-router-scroll-top";

import { Context } from './store';

import { Howl } from 'howler';
import ringtone from './ringtone.mp3';
import { useSelector } from "react-redux";
import { indexRoutes } from './routes';
import PrivateRoute from './routes/PrivateRoute';
import PublicRoute from './routes/PublicRoute';
import GlobalCallManager from './components/GlobalCallManager';
import ChatInterface from './components/Chat/ChatInterface';
import { socket } from './Socket';
import { DeleteRequestModal } from './components/DeleteRequestModalComponent/DeleteRequestModal';

const ringtoneSound = new Howl({
  src: [ringtone],
  loop: true,
  preload: true
})

const App = () => {
  const location = window.location.pathname;
  const [calling, setCalling] = React.useState(false);
  const [doctorProifleImage] = useState('');
  const [doctorName] = React.useState('');
  const [callType] = useState('audio');
  const [state] = useContext(Context);
  const userData = useSelector((state) => state.auth.userData);
  const {pathName} = useSelector(state => state.router)
  
  // Global WebRTC Call Management
  const [globalCallData, setGlobalCallData] = useState(null);
  const [showGlobalCallScreen, setShowGlobalCallScreen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  React.useEffect(() => {
    if(pathName !== location && +localStorage.routeToBeUpdate) {
      localStorage.routeToBeUpdate = 0
      window.location.href = pathName
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName])

  React.useEffect(() => {
    const timezoneString = new Intl.DateTimeFormat().resolvedOptions().timeZone;
   localStorage.setItem("timezone", timezoneString)
  }, [userData]);

  React.useEffect(() => {

    // Check for user data from multiple sources
    let user = null;

    // First check Redux userData
    if (userData && userData._id) {
      user = userData;
    } else {
      const userTypes = ['customer', 'doctor', 'organization', 'subuser', 'emergencyContact'];

      for (const userType of userTypes) {
        try {
          const storedData = localStorage.getItem(userType);

          if (storedData) {
            const storedUser = JSON.parse(storedData);

            if (storedUser && storedUser._id) {
              user = storedUser;
              break;
            }
          }
        } catch (error) {
        }
      }
    }

    if (user && user._id) {
      // Extract user information
      const userRole = user.role || user.userType || 'patient';

      // Try multiple name fields
      let userName = 'Unknown User';
      if (user.name) userName = user.name;
      else if (user.firstName) userName = user.firstName;
      else if (user.username) userName = user.username;
      else if (user.email) userName = user.email.split('@')[0];
      else userName = `User ${user._id.slice(-4)}`;

      // Set current user for global call management
      setCurrentUser({
        socketId: user._id,
        name: userName,
        role: userRole,
        _id: user._id
      });

      const connectionData = {
        userId: user._id,
        name: userName,
        role: userRole
      };

      // Helper: register this user in the server's activeUsers map.
      // Must be called after every (re)connection because socket.id changes on reconnect
      // and the old entry in activeUsers gets removed by the disconnect handler.
      const registerUser = () => {
        if (userRole === 'doctor') {
          socket.emit('join_doctor', connectionData);
        } else {
          socket.emit('join_patient', connectionData);
        }
        socket.emit('user-connect', { userId: user._id, name: userName });
      };

      // Connect socket when user is logged in
      if (!socket.connected) {
        socket.connect();
      }

      // Register immediately (socket.io buffers the emit if not yet connected)
      registerUser();

      // Re-register whenever the socket reconnects so activeUsers always has a live entry
      socket.on('connect', registerUser);

      return () => {
        socket.off('connect', registerUser);
      };
    } else {
      console.log('🌍 No valid user data found for socket connection');
    }
  }, [userData]) // Add userData as dependency
  
  // Listen for logout events to disconnect socket
  React.useEffect(() => {
    const handleStorageChange = () => {
      // Check all possible user types that could be stored
      const userTypes = ['customer', 'doctor', 'organization', 'subuser', 'emergencyContact'];
      let hasValidUser = false;
      
      for (const userType of userTypes) {
        try {
          const userData = JSON.parse(localStorage.getItem(userType));
          if (userData && userData._id) {
            hasValidUser = true;
            break;
          }
        } catch (error) {
          // Ignore JSON parse errors
        }
      }
      
      if (!hasValidUser) {
        // User logged out, disconnect socket
        if (socket.connected) {
          socket.disconnect();
          setCurrentUser(null);
        }
      }
    };
    
    // Custom logout event listener
    const handleLogoutEvent = () => {
      if (socket.connected) {
        socket.disconnect();
        setCurrentUser(null);
      }
    };
    
    // Listen for localStorage changes (logout)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom logout events
    window.addEventListener('user-logged-out', handleLogoutEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-logged-out', handleLogoutEvent);
    };
  }, []);

  // WebRTC Call Handlers (replaces old Agora RTC)
  const handleClickJoin = () => {
    setCalling(false);
    ringtoneSound.stop();
    ringtoneSound.unload();
  }

  const callRejected = () => {
    setCalling(false);
    ringtoneSound.stop();
    ringtoneSound.unload();
  }

  // Global WebRTC Call Handlers
  const handleGlobalCallAccept = (callData) => {
    setGlobalCallData(callData);
    setShowGlobalCallScreen(true);
  };

  const handleGlobalCallReject = (callData) => {
    setGlobalCallData(null);
    setShowGlobalCallScreen(false);
  };

  const closeGlobalCallScreen = () => {
    setShowGlobalCallScreen(false);
    setGlobalCallData(null);
  };

  return (
    <>
      <ScrollToTop>
        <Switch>
          <Loader
            show={state.loading}
            message={<div className="spinner-border"></div>}
            backgroundStyle={{ position: "fixed", zIndex: 10000 }}
          >
            {indexRoutes.map((prop, key) => {
              return prop.private ? <PrivateRoute
                path={prop.path}
                exact={true}
                key={key}
                name={prop}
                component={prop.component}
                private={prop.private}
              />
                :
                <PublicRoute
                  path={prop.path}
                  exact={true}
                  key={key}
                  name={prop}
                  component={prop.component}
                />
            })}
          </Loader>

        </Switch>
      </ScrollToTop>
      {calling ? (
        <div className="modal fade call-modal show" id="video_call" aria-modal="true" style={{ display: 'block', background: 'rgb(80 88 80 / 10%)', backdropFilter: 'blur(2px)', paddingLeft: '0px' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-body" style={{ padding: '40px' }}>
                <div className="call-box incoming-box">
                  <div className="call-wrapper" style={{ height: 'auto' }}>
                    <div className="call-inner">
                      <div className="call-user">
                        <img className="call-avatar" src={doctorProifleImage ? `${doctorProifleImage}` : "/assets/img/doctor-empty.png"} alt="User Image" />
                        <h4>{doctorName}</h4>
                        <span>Calling ...</span>
                      </div>
                      <div className="call-items">
                        <a href="javascript:void(0);" className="btn call-item call-end" data-dismiss="modal" aria-label="Close"><i className="material-icons" onClick={callRejected}>call_end</i></a>
                        {callType === 'audio' ? (
                          <a href="javascript:void(0);" onClick={() => handleClickJoin()} data-toggle="modal" data-target="#video_call" className="btn call-item call-start"><i className="material-icons">phone_in_talk</i></a>
                        ) : (
                          <a href="javascript:void(0);" onClick={() => handleClickJoin()} data-toggle="modal" data-target="#video_call" className="btn call-item call-start"><i className="material-icons">videocam</i></a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      ) : null}
      
      {/* Global WebRTC Call Manager - Shows incoming call notifications anywhere in the app */}
      <GlobalCallManager 
        currentUser={currentUser}
        onCallAccept={handleGlobalCallAccept}
        onCallReject={handleGlobalCallReject}
      />
      
      {/* Global Call Screen - Full WhatsApp-style chat interface when call is accepted */}
      {showGlobalCallScreen && globalCallData && (
        <ChatInterface
          currentUser={currentUser}
          onClose={closeGlobalCallScreen}
          initialReceiver={{
            socketId: globalCallData.callerId,
            name: globalCallData.callerName,
            _id: globalCallData.callerId,
            appointmentId: globalCallData.appointmentId,
            incomingCallData: globalCallData // Pass call data for auto-accept
          }}
          appointmentData={globalCallData.appointmentData} // Pass appointment data for proper messaging
        />
      )}

      <DeleteRequestModal  />
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default App;
